<script setup>
import { ref, watch } from 'vue'
import { reset } from '@formkit/core'
import { PROJECT } from '@/api'
import { useToast } from 'vue-toastification'

const toast = useToast()

const props = defineProps({
  project: {
    type: Object,
    required: true,
    default: {},
  },
})

const buttonText = ref('Create')

const action = ref('update')

const errors = ref([])

const emit = defineEmits(['close', 'refresh'])

const actionOptions = ref([])

watch(
  () => props.project,
  async value => {
    if (value?.id) {
      if (value.accountings.length) {
        actionOptions.value = ['update', 'submit']
      } else {
        actionOptions.value = ['delete', 'update']
      }
    } else {
      actionOptions.value = []
    }
  },
)

const formData = ref({
  name: null,
  country: null,
  begin_date: null,
  end_date: null,
})

function closeModal() {
  buttonText.value = 'Create'
  emit('close')
  reset('project')
}

const isDisabled = status => {
  const disabledStatus = ['submit', 'delete']
  return disabledStatus.includes(status)
}

const submitHandler = async () => {
  errors.value = []
  try {
    if (props.project?.id) {
      await actionSwitch(action.value)
      toast.success(`${action.value} project`)
    } else {
      // create
      const { data } = await PROJECT.post(formData.value)
      toast.success(`created successfully`)
    }
    emit('refresh', props.project?.id)
    closeModal()
  } catch (e) {
    if (e.response.data.status === 400) {
      errors.value.push(e.response.data.message)
      return
    }
    Object.keys(e.response.data.errors).forEach(key => {
      errors.value.push(e.response.data.errors[key].join('.'))
    })
  }
}

const actionSwitch = async actionName => {
  switch (actionName) {
    case 'update':
      await PROJECT.update(props.project?.id, formData.value)
      break
    case 'delete':
      await PROJECT.delete(props.project?.id)
      break
    case 'submit':
      await PROJECT.submit(props.project?.id, formData.value)
      break
  }
}

watch(
  () => props.project,
  value => {
    if (Object.keys(value).length) {
      formData.value.name = value.name
      formData.value.country = value.country
      formData.value.begin_date = dayjs(value.begin_date).format('YYYY-MM-DD')
      formData.value.end_date = dayjs(value.end_date).format('YYYY-MM-DD')
      buttonText.value = 'update'
    }
  },
)
</script>


<template>
    <div class="bg-white p-4 w-[90vw] max-w-sm rounded max-h-[95vh]"
    >
        <div class="flex justify-between">
            <div class="px-2 py-1 bg-gray-200 rounded shadow w-fit">
                <div v-if="project.status === 'submit'">
                    {{ action }}
                </div>
                <div v-else>
                    {{ project?.id ? 'update' : 'create' }}
                </div>
            </div>
            <button @click="closeModal">❌</button>
        </div>
        <div
            class="mt-4"
        >
            <FormKit
                :disabled="isDisabled(project.status)"
                type="form"
                id="project"
                v-model="formData"
                submit-label="confirm"
                :errors="errors"
                @submit="submitHandler"
            >
                <FormKit
                    type="text"
                    name="name"
                    label="Project name"
                    placeholder="e.g 20221010-INTEL"
                    help="Suggest: date + event"
                    validation="required"
                />

                <FormKit
                    type="text"
                    name="country"
                    label="Country"
                    placeholder="e.g Japan"
                    help="required, if not in taiwan"
                />

                <FormKit
                    type="date"
                    :value="new Date()"
                    name="begin_date"
                    label="Begin date"
                    validation="required"
                    validation-visibility="live"
                />

                <FormKit
                    type="date"
                    :value="new Date()"
                    name="end_date"
                    label="End date"
                    validation="required"
                    validation-visibility="live"
                />
                <FormKit
                    v-if="buttonText !=='Create'"
                    type="select"
                    label="Action"
                    help="Update project/ Submit accounting of project / Delete project"
                    v-model="action"
                    :options="actionOptions"
                />
            </FormKit>
        </div>
    </div>

</template>
